import type { FC, ReactNode } from 'react'
import type { PhotoId } from '@/components/generals/photo'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { clamp, onScrollFrame } from '@/lib/scroll'
import { Photo } from '@/components/generals/photo'
import { routes } from '@/constants/site'

interface Designer {
    name: string
    site: string
    photo: PhotoId
}

const DESIGNERS: Designer[] = [
    { name: 'Oren Abadi', site: 'orenabadi.com', photo: 'luca' },
    { name: 'Sol Marquez', site: 'solmarquez.design', photo: 'elena' },
    { name: 'Lucas Ferreira', site: 'lucasferreira.studio', photo: 'mateo' },
    { name: 'Dara Quinn', site: 'daraquinn.io', photo: 'sofia' },
    { name: 'Hugo Brandt', site: 'hugobrandt.dev', photo: 'adrian' },
    { name: 'Inès Laurent', site: 'ineslaurent.co', photo: 'nora' }
]

/* A loose pile: small offsets and turns for each sticker before it flies to its seat */
const PILE = [
    { x: -18, y: 8, r: -14 },
    { x: 22, y: -10, r: 11 },
    { x: -6, y: -22, r: -6 },
    { x: 14, y: 18, r: 17 },
    { x: -26, y: -4, r: 8 },
    { x: 6, y: 4, r: -19 },
    { x: 28, y: 12, r: -4 },
    { x: -4, y: -12, r: 13 }
]

const SEAT_TURN = [-2.5, 1.8, -1.2, 2.6, -1.8, 1.4, -2.2, 2]

/* Four seats across: as large as the design's seat, never wider than a quarter of the row */
const SEAT_SIZE = 'md:w-[min(clamp(120px,15vh,176px),calc((100vw_-_2*clamp(16px,3vw,32px)_-_3*clamp(16px,3vw,44px))/4))]'
const ROW = 'contents md:flex md:justify-center md:gap-x-[clamp(16px,3vw,44px)]'

const easeInOut = (value: number): number => {
    return value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2
}

/** The seat: a frayed square of card that the photo print lands on. */
const SeatPaper: FC = (): ReactNode => {
    return <span aria-hidden='true' className='fray absolute inset-[7px] rounded-[2px] bg-card' />
}

interface PhotoPrintProps {
    designer: Designer
}

/** The designer's photo as a small print in a paper border. */
const PhotoPrint: FC<PhotoPrintProps> = ({ designer }): ReactNode => {
    return (
        <span className='block rounded-[4px] bg-paper p-[5%] shadow-[0_1px_2px_rgb(40_38_34/0.12),0_14px_26px_-14px_rgb(40_38_34/0.5)]'>
            <Photo id={designer.photo} sizes='176px' className='aspect-square w-full rounded-[2px]' />
        </span>
    )
}

interface SeatProps {
    designer: Designer
    index: number
    /** Static seats hold their print; animated seats wait for it to fly in. */
    holdPrint: boolean
    labelShown: boolean
    seatRef?: (element: HTMLDivElement | null) => void
    labelRef?: (element: HTMLDivElement | null) => void
}

const Seat: FC<SeatProps> = ({ designer, index, holdPrint, labelShown, seatRef, labelRef }): ReactNode => {
    return (
        <li className={cn('text-center', SEAT_SIZE)}>
            <div ref={seatRef} className='relative mx-auto aspect-square w-[clamp(120px,15vh,176px)] max-w-full md:w-full'>
                <SeatPaper />
                {holdPrint && (
                    <div className='absolute inset-[8%] z-[1]' style={{ rotate: `${SEAT_TURN[index]}deg` }}>
                        <PhotoPrint designer={designer} />
                    </div>
                )}
            </div>

            <div
                ref={labelRef}
                data-on={labelShown ? '' : undefined}
                className='mt-1.5 translate-y-1.5 opacity-0 transition-[opacity,translate] duration-150 ease-out data-[on]:translate-y-0 data-[on]:opacity-100 data-[on]:duration-[350ms] data-[on]:ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none'
            >
                <h3 className='font-serif text-[16px] leading-[1.3] tracking-[-0.01em] text-ink'>
                    {designer.name}
                </h3>
                <p className='mx-auto mt-[3px] text-[13px] leading-4 text-ink/[0.78] underline decoration-ink/30 underline-offset-[3px]'>
                    {designer.site}
                </p>
            </div>
        </li>
    )
}

/** The ninth seat is kept open: on phones it closes the class as a wide band. */
const OpenSeat: FC = (): ReactNode => {
    return (
        <li className={cn('col-span-2 text-center md:col-span-1', SEAT_SIZE)}>
            <Link
                href={routes.getStarted}
                className='group relative mx-auto block h-24 w-full rounded-[2px] md:aspect-square md:h-auto'
            >
                <SeatPaper />
                <span className='hand absolute inset-0 z-[1] flex items-center justify-center px-[10%] text-center text-[clamp(17px,1.55vw,21px)] leading-[1.35] text-ink/55 transition-colors duration-200 [rotate:-3deg] group-hover:text-ink/80'>
                    <span>
                        Your <br className='max-md:hidden' />
                        next hire?
                    </span>
                </span>
            </Link>
        </li>
    )
}

/**
 * Scouted, then hired. With a fine pointer and motion allowed (768px and up), the photo prints lie in a
 * pile and fly to their seats one by one as you scroll through a pinned stage. Otherwise the
 * class renders already sorted.
 */
export const ScoutedThenHired: FC = (): ReactNode => {
    const [animated, setAnimated] = useState(false)
    const trackRef = useRef<HTMLElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const seatRefs = useRef<Array<HTMLDivElement | null>>([])
    const stickerRefs = useRef<Array<HTMLDivElement | null>>([])
    const labelRefs = useRef<Array<HTMLDivElement | null>>([])

    useEffect(() => {
        const query = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px) and (prefers-reduced-motion: no-preference)')
        const update = () => setAnimated(query.matches)

        update()
        query.addEventListener('change', update)

        return () => query.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        const track = trackRef.current
        const stage = stageRef.current

        if (!animated || !track || !stage) return

        let seats: Array<{ x: number, y: number }> = []
        let width = 0
        let height = 0
        let center = { x: 0, y: 0 }

        const measure = () => {
            const stageRect = stage.getBoundingClientRect()
            const first = stickerRefs.current[0]

            width = first?.offsetWidth ?? 0
            height = first?.offsetHeight ?? 0
            center = { x: stageRect.width / 2, y: stageRect.height * 0.46 }
            seats = seatRefs.current.map(seat => {
                const rect = seat?.getBoundingClientRect()

                if (!rect) return { x: 0, y: 0 }

                return { x: rect.left - stageRect.left + rect.width / 2, y: rect.top - stageRect.top + rect.height / 2 }
            })
        }

        const render = () => {
            const rect = track.getBoundingClientRect()
            const progress = clamp(-rect.top / Math.max(1, rect.height - window.innerHeight))

            stickerRefs.current.forEach((sticker, index) => {
                const label = labelRefs.current[index]
                const seat = seats[index]

                if (!sticker || !seat) return

                const local = clamp((progress - 0.05 - index * 0.075) / 0.32)
                const eased = easeInOut(local)
                const arc = Math.sin(Math.PI * local)
                const pile = PILE[index]
                const fromX = center.x - width / 2 + pile.x
                const fromY = center.y - height / 2 + pile.y
                const toX = seat.x - width / 2
                const toY = seat.y - height / 2
                const x = fromX + (toX - fromX) * eased
                const y = fromY + (toY - fromY) * eased - arc * 40
                const turn = pile.r + (SEAT_TURN[index] - pile.r) * eased
                const tiltX = arc * 26
                const tiltY = arc * (index % 2 === 0 ? -20 : 20)
                const scale = (0.9 + 0.1 * eased) * (1 + arc * 0.12)

                sticker.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) rotateX(${tiltX.toFixed(2)}deg) rotateY(${tiltY.toFixed(2)}deg) rotate(${turn.toFixed(2)}deg) scale(${scale.toFixed(4)})`
                sticker.style.zIndex = local > 0 && local < 1 ? String(30 + index) : local >= 1 ? '10' : String(20 - index)

                if (label) {
                    if (local >= 0.98) label.setAttribute('data-on', '')
                    else label.removeAttribute('data-on')
                }
            })
        }

        measure()
        render()

        const unsubscribe = onScrollFrame(render)
        const handleResize = () => {
            measure()
            render()
        }

        window.addEventListener('resize', handleResize)
        document.fonts?.ready.then(handleResize).catch(() => undefined)

        return () => {
            unsubscribe()
            window.removeEventListener('resize', handleResize)
        }
    }, [animated])

    const seat = (designer: Designer, index: number): ReactNode => (
        <Seat
            key={designer.name}
            designer={designer}
            index={index}
            holdPrint={!animated}
            labelShown={!animated}
            seatRef={element => {
                seatRefs.current[index] = element
            }}
            labelRef={element => {
                labelRefs.current[index] = element
            }}
        />
    )

    return (
        <section
            ref={trackRef}
            id='scouted-then-hired'
            aria-labelledby='scouted-title'
            className={cn(
                'relative mb-[clamp(12px,1.5vw,24px)] mt-[clamp(32px,4vw,56px)] max-[991px]:mt-[55px] max-md:mt-8',
                animated && 'h-[180vh]'
            )}
        >
            <div
                className={cn(
                    'section-x mx-auto flex max-w-[calc(1080px+2*clamp(16px,3vw,32px))] flex-col text-center',
                    animated ? 'sticky top-0 h-screen justify-center pt-[clamp(20px,3vh,40px)]' : 'pb-6 pt-5'
                )}
            >
                <div className='reveal flex flex-col items-center'>
                    <h2 id='scouted-title' className='type-section'>
                        Scouted, <span className='text-ink/[0.78]'>then hired</span>
                    </h2>
                    <p className='hand mb-4 mt-0.5 inline-block text-[clamp(30px,3vw,42px)] leading-[1.2] text-ink/[0.72] [rotate:-3deg]'>
                        Class of 2026
                    </p>
                </div>

                <div ref={stageRef} className='relative mx-auto mt-2 w-full max-w-[1080px]'>
                    <div className='grid grid-cols-[repeat(2,auto)] justify-center gap-x-[clamp(32px,6vw,88px)] gap-y-6 py-6 md:flex md:flex-col md:gap-y-[clamp(8px,1.6vh,18px)] md:py-0'>
                        <ul className={ROW}>
                            {DESIGNERS.slice(0, 4).map((designer, index) => seat(designer, index))}
                        </ul>
                        <ul className={ROW}>
                            {DESIGNERS.slice(4).map((designer, index) => seat(designer, index + 4))}
                            <OpenSeat />
                        </ul>
                    </div>

                    {animated && (
                        <div aria-hidden='true' className='pointer-events-none absolute inset-0 [perspective:1200px]'>
                            {DESIGNERS.map((designer, index) => (
                                <div
                                    key={designer.name}
                                    ref={element => {
                                        stickerRefs.current[index] = element
                                    }}
                                    className='absolute left-0 top-0 w-[calc(min(clamp(120px,15vh,176px),calc((100vw_-_2*clamp(16px,3vw,32px)_-_3*clamp(16px,3vw,44px))/4))*0.84)] will-change-transform [transform-origin:50%_50%]'
                                >
                                    <PhotoPrint designer={designer} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}
